import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/store';
import { createKeyResult, updateKeyResult, deleteKeyResult } from '@/store/slices/okrsSlice';
import { GlassCard, GlassBadge, GlassButton } from '@/design-system';
import { Input } from '@/components/ui';
import {
  Target,
  Building2,
  Layers,
  FileCode,
  Edit2,
  Plus,
  TrendingUp,
  Trash2,
  Calendar,
  Link2,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import type { Objective, KeyResult, OKRLevel, OKRStatusNew } from '@/types';

interface ObjectiveDetailModalProps {
  objective: Objective;
  onEdit: () => void;
  onClose: () => void;
  onProgressUpdate: (keyResult: KeyResult) => void;
  getLevelColor: (level?: OKRLevel) => string;
  getStatusColor: (status: OKRStatusNew) => string;
}

const ObjectiveDetailModal: React.FC<ObjectiveDetailModalProps> = ({
  objective,
  onEdit,
  onClose,
  onProgressUpdate,
  getLevelColor,
  getStatusColor,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = useState<'overview' | 'keyresults' | 'alignment'>('overview');
  const [isAddingKR, setIsAddingKR] = useState(false);
  const [newKR, setNewKR] = useState({
    description: '',
    targetValue: 100,
    startValue: 0,
    currentValue: 0,
    unit: '%',
    confidence: 50,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getLevelIcon = (level?: OKRLevel) => {
    switch (level) {
      case 'IS':
        return <Layers className="w-5 h-5" />;
      case 'PROGRAM':
        return <Building2 className="w-5 h-5" />;
      case 'EPIC':
        return <FileCode className="w-5 h-5" />;
      default:
        return <Target className="w-5 h-5" />;
    }
  };

  const getProgressColor = (progress: number | null | undefined) => {
    if (progress === null || progress === undefined) return 'bg-gray-500';
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const handleAddKeyResult = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await dispatch(createKeyResult({
        ...newKR,
        objectiveId: objective.id,
      })).unwrap();

      setIsAddingKR(false);
      setNewKR({
        description: '',
        targetValue: 100,
        startValue: 0,
        currentValue: 0,
        unit: '%',
        confidence: 50,
      });
    } catch (err) {
      console.error('Failed to create key result:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteKeyResult = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this key result?')) return;
    try {
      await dispatch(deleteKeyResult(id)).unwrap();
    } catch (err) {
      console.error('Failed to delete key result:', err);
    }
  };

  const keyResults = objective.keyResults || [];
  const progress = objective.overallProgress ?? 0;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'keyresults', label: `Key Results (${keyResults.length})` },
    { id: 'alignment', label: 'Alignment' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div
            className={`p-3 rounded-lg bg-${getLevelColor(objective.level)}-500/20 text-${getLevelColor(objective.level)}-400`}
          >
            {getLevelIcon(objective.level)}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-2">{objective.title}</h2>
            <div className="flex items-center gap-3 flex-wrap">
              <GlassBadge variant={getStatusColor(objective.status) as any}>
                {objective.status}
              </GlassBadge>
              <GlassBadge variant="default">
                {objective.level || 'Unknown'} Level
              </GlassBadge>
              <span className="text-sm text-muted-foreground">
                Q{objective.quarter} FY{objective.fiscalYear.toString().slice(-2)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-3xl font-bold text-foreground">{Math.round(progress)}%</div>
            <div className="text-sm text-muted-foreground">Overall Progress</div>
          </div>
          <GlassButton variant="secondary" onClick={onEdit}>
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </GlassButton>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-3 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden">
        <div
          className={`h-full ${getProgressColor(progress)} transition-all duration-300`}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--border-primary)]">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {objective.description && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Description</h4>
                <p className="text-muted-foreground">{objective.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Details</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Level</span>
                    <span className="text-foreground">{objective.level || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Entity</span>
                    <span className="text-foreground">{objective.entityName || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Quarter</span>
                    <span className="text-foreground">Q{objective.quarter} FY{objective.fiscalYear.toString().slice(-2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <GlassBadge variant={getStatusColor(objective.status) as any}>
                      {objective.status}
                    </GlassBadge>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Key Metrics</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Key Results</span>
                    <span className="text-foreground">{keyResults.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Avg Progress</span>
                    <span className="text-foreground">{Math.round(progress)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Child Objectives</span>
                    <span className="text-foreground">{objective.childObjectives?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Key Results Tab */}
        {activeTab === 'keyresults' && (
          <div className="space-y-4">
            {/* Add Key Result Button */}
            <div className="flex justify-end">
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => setIsAddingKR(true)}
                disabled={isAddingKR}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Key Result
              </GlassButton>
            </div>

            {/* Add Key Result Form */}
            {isAddingKR && (
              <GlassCard className="p-4">
                <form onSubmit={handleAddKeyResult} className="space-y-4">
                  <h4 className="text-sm font-medium text-foreground">New Key Result</h4>

                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Description</label>
                    <Input
                      value={newKR.description}
                      onChange={(e) => setNewKR({ ...newKR, description: e.target.value })}
                      placeholder="What measurable result will you achieve?"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Start Value</label>
                      <Input
                        type="number"
                        value={newKR.startValue}
                        onChange={(e) => setNewKR({ ...newKR, startValue: parseFloat(e.target.value) })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Target Value</label>
                      <Input
                        type="number"
                        value={newKR.targetValue}
                        onChange={(e) => setNewKR({ ...newKR, targetValue: parseFloat(e.target.value) })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Unit</label>
                      <Input
                        value={newKR.unit}
                        onChange={(e) => setNewKR({ ...newKR, unit: e.target.value })}
                        placeholder="%, users, $"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <GlassButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsAddingKR(false)}
                    >
                      Cancel
                    </GlassButton>
                    <GlassButton
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Adding...' : 'Add Key Result'}
                    </GlassButton>
                  </div>
                </form>
              </GlassCard>
            )}

            {/* Key Results List */}
            {keyResults.length === 0 ? (
              <div className="text-center py-8">
                <Target className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No key results yet</p>
                <p className="text-sm text-muted-foreground">Add key results to track progress</p>
              </div>
            ) : (
              <div className="space-y-3">
                {keyResults.map((kr) => (
                  <GlassCard key={kr.id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-foreground font-medium">{kr.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span>
                            {kr.currentValue ?? kr.startValue} / {kr.targetValue} {kr.unit || ''}
                          </span>
                          {kr.confidence !== null && kr.confidence !== undefined && (
                            <span className="flex items-center gap-1">
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  kr.confidence >= 70 ? 'bg-green-500' :
                                  kr.confidence >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                              />
                              {kr.confidence}% confidence
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-lg font-bold text-foreground">{Math.round(kr.progress ?? 0)}%</div>
                          <div className="w-20 h-1.5 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${getProgressColor(kr.progress)} transition-all duration-300`}
                              style={{ width: `${Math.min(kr.progress ?? 0, 100)}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex gap-1">
                          <button
                            onClick={() => onProgressUpdate(kr)}
                            className="p-2 rounded hover:bg-[var(--bg-hover)]/50 text-muted-foreground hover:text-foreground transition-colors"
                            title="Update Progress"
                          >
                            <TrendingUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteKeyResult(kr.id)}
                            className="p-2 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </GlassCard>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Alignment Tab */}
        {activeTab === 'alignment' && (
          <div className="space-y-6">
            {/* Parent Objective */}
            {objective.parentObjective && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Aligned To
                </h4>
                <GlassCard className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <Target className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-foreground font-medium">{objective.parentObjective.title}</p>
                      {objective.parentObjective.status && (
                        <GlassBadge variant={getStatusColor(objective.parentObjective.status) as any} className="mt-1">
                          {objective.parentObjective.status}
                        </GlassBadge>
                      )}
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}

            {/* Child Objectives */}
            {objective.childObjectives && objective.childObjectives.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Aligned Objectives ({objective.childObjectives.length})
                </h4>
                <div className="space-y-2">
                  {objective.childObjectives.map((child) => (
                    <GlassCard key={child.id} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded-md bg-${getLevelColor(child.level)}-500/20`}>
                            {getLevelIcon(child.level)}
                          </div>
                          <div>
                            <p className="text-sm text-foreground">{child.title}</p>
                            <span className="text-xs text-muted-foreground">{child.level} Level</span>
                          </div>
                        </div>
                        <div className="text-sm text-foreground font-medium">
                          {Math.round(child.overallProgress ?? 0)}%
                        </div>
                      </div>
                    </GlassCard>
                  ))}
                </div>
              </div>
            )}

            {!objective.parentObjective && (!objective.childObjectives || objective.childObjectives.length === 0) && (
              <div className="text-center py-8">
                <Link2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No alignment data</p>
                <p className="text-sm text-muted-foreground">This objective is not aligned to any parent or children</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjectiveDetailModal;
